import React, { useEffect, useState } from 'react';

// Clipboard helper that works on HTTP origins (navigator.clipboard is only
// available on HTTPS or localhost). Falls back to a hidden textarea +
// document.execCommand('copy') so staff using the LAN IP (http://192.168.x.x)
// don't hit "Cannot read properties of undefined (reading 'writeText')".
const copyText = async (text) => {
  const s = String(text ?? '');
  try {
    if (navigator?.clipboard?.writeText) {
      await navigator.clipboard.writeText(s);
      return true;
    }
  } catch { /* fall through to textarea fallback */ }
  try {
    const ta = document.createElement('textarea');
    ta.value = s;
    ta.setAttribute('readonly', '');
    ta.style.position = 'fixed';
    ta.style.opacity = '0';
    document.body.appendChild(ta);
    ta.select();
    const ok = document.execCommand('copy');
    document.body.removeChild(ta);
    return ok;
  } catch { return false; }
};

function ChannelManagerSettings({ token }) {
  const [form, setForm] = useState({
    channel_manager_name: 'EASEROOM',
    api_url: '',
    post_header: '',
    roomtypes_url: '',
    rateplans_url: '',
    booking_confirm_url: '',
    inventory_update_url: '',
    rate_update_url: '',
    inventory_rates_url: '',
    booking_push_url: '',
    no_show_update_url: ''
  });
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');

  // Security section state
  const [allowedIps, setAllowedIps] = useState(''); // comma-separated in DB
  const [ipInput, setIpInput] = useState('');
  const [ipList, setIpList] = useState([]);
  const [secSaving, setSecSaving] = useState(false);
  const [secMessage, setSecMessage] = useState('');
  const [secError, setSecError] = useState('');
  const [copied, setCopied] = useState(false);

  // SSO section state
  const [ssoServiceKey, setSsoServiceKey] = useState('');
  const [ssoKeyVisible, setSsoKeyVisible] = useState(false);
  const [ssoIpList, setSsoIpList] = useState([]);
  const [ssoIpInput, setSsoIpInput] = useState('');
  const [cmBaseUrl, setCmBaseUrl] = useState('');
  const [cmSsoLoginUrl, setCmSsoLoginUrl] = useState('');
  const [ssoSaving, setSsoSaving] = useState(false);
  const [ssoMessage, setSsoMessage] = useState('');
  const [ssoError, setSsoError] = useState('');

  const callbackUrl = `${window.location.origin}/api/v1/channel-manager/booking-callback`;

  const headers = () => ({ 'Content-Type': 'application/json', Authorization: `Bearer ${token}` });

  useEffect(() => {
    setLoading(true);
    fetch('/api/admin/channel-manager', { headers: headers() })
      .then((res) => res.json().then((data) => ({ ok: res.ok, data })))
      .then(({ ok, data }) => {
        if (ok && data) {
          setForm({
            channel_manager_name: data.channel_manager_name || 'EASEROOM',
            api_url: data.api_url || '',
            post_header: data.post_header || '',
            roomtypes_url: data.roomtypes_url || '',
            rateplans_url: data.rateplans_url || '',
            booking_confirm_url: data.booking_confirm_url || '',
            inventory_update_url: data.inventory_update_url || '',
            rate_update_url: data.rate_update_url || '',
            inventory_rates_url: data.inventory_rates_url || '',
            booking_push_url: data.booking_push_url || '',
            no_show_update_url: data.no_show_update_url || ''
          });
          const ips = (data.allowed_ips || '').split(',').map((s) => s.trim()).filter(Boolean);
          setIpList(ips);
          setAllowedIps(data.allowed_ips || '');
          // SSO fields
          setSsoServiceKey(data.sso_service_key || '');
          const ssoIps = (data.sso_allowed_ips || '').split(',').map((s) => s.trim()).filter(Boolean);
          setSsoIpList(ssoIps);
          setCmBaseUrl(data.cm_base_url || '');
          setCmSsoLoginUrl(data.cm_sso_login_url || '');
        }
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const isValidUrl = (val) => { try { new URL(val.trim()); return true; } catch { return false; } };

  const validate = () => {
    if (!form.channel_manager_name.trim()) return 'Channel Manager Name is required';
    if (!form.api_url.trim()) return 'API Base URL is required';
    if (!isValidUrl(form.api_url)) return 'API Base URL must be a valid URL';
    if (form.api_url.trim().length > 500) return 'API Base URL must not exceed 500 characters';
    if (form.roomtypes_url.trim() && !isValidUrl(form.roomtypes_url)) return 'Get RoomTypes URL must be a valid URL';
    if (form.roomtypes_url.trim().length > 500) return 'Get RoomTypes URL must not exceed 500 characters';
    if (form.rateplans_url.trim() && !isValidUrl(form.rateplans_url)) return 'Get RatePlans URL must be a valid URL';
    if (form.rateplans_url.trim().length > 500) return 'Get RatePlans URL must not exceed 500 characters';
    if (form.booking_confirm_url.trim() && !isValidUrl(form.booking_confirm_url)) return 'Booking Status Confirm URL must be a valid URL';
    if (form.booking_confirm_url.trim().length > 500) return 'Booking Status Confirm URL must not exceed 500 characters';
    if (form.inventory_update_url.trim() && !isValidUrl(form.inventory_update_url)) return 'Inventory Update URL must be a valid URL';
    if (form.inventory_update_url.trim().length > 500) return 'Inventory Update URL must not exceed 500 characters';
    if (form.rate_update_url.trim() && !isValidUrl(form.rate_update_url)) return 'Rate Update URL must be a valid URL';
    if (form.rate_update_url.trim().length > 500) return 'Rate Update URL must not exceed 500 characters';
    if (form.inventory_rates_url.trim() && !isValidUrl(form.inventory_rates_url)) return 'Get Inventory & Rates URL must be a valid URL';
    if (form.inventory_rates_url.trim().length > 500) return 'Get Inventory & Rates URL must not exceed 500 characters';
    if (form.booking_push_url.trim() && !isValidUrl(form.booking_push_url)) return 'Booking Push URL must be a valid URL';
    if (form.booking_push_url.trim().length > 500) return 'Booking Push URL must not exceed 500 characters';
    if (form.no_show_update_url.trim() && !isValidUrl(form.no_show_update_url)) return 'No Show Update URL must be a valid URL';
    if (form.no_show_update_url.trim().length > 500) return 'No Show Update URL must not exceed 500 characters';
    if (form.post_header.trim()) {
      if (form.post_header.trim().length > 500) return 'Post Header must not exceed 500 characters';
      try { JSON.parse(form.post_header.trim()); } catch { return 'Post Header must be valid JSON (e.g. {"Username":"test","Password":"test"})'; }
    }
    return '';
  };

  const handleSubmit = async () => {
    setError(''); setMessage('');
    const err = validate();
    if (err) { setError(err); return; }
    setSaving(true);
    try {
      const res = await fetch('/api/admin/channel-manager', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          channel_manager_name: form.channel_manager_name.trim(),
          api_url: form.api_url.trim(),
          post_header: form.post_header.trim() || null,
          roomtypes_url: form.roomtypes_url.trim() || null,
          rateplans_url: form.rateplans_url.trim() || null,
          booking_confirm_url: form.booking_confirm_url.trim() || null,
          inventory_update_url: form.inventory_update_url.trim() || null,
          rate_update_url: form.rate_update_url.trim() || null,
          inventory_rates_url: form.inventory_rates_url.trim() || null,
          booking_push_url: form.booking_push_url.trim() || null,
          no_show_update_url: form.no_show_update_url.trim() || null,
          allowed_ips: ipList.join(',') || null
        })
      });
      const data = await res.json();
      if (!res.ok) { setError(data?.error || 'Failed to save'); return; }
      setMessage('Channel Manager settings saved successfully.');
    } catch {
      setError('Network error while saving');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveSecurity = async () => {
    setSecError(''); setSecMessage('');
    setSecSaving(true);
    try {
      const res = await fetch('/api/admin/channel-manager', {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({
          channel_manager_name: form.channel_manager_name.trim() || 'EASEROOM',
          api_url: form.api_url.trim() || 'http://placeholder',
          post_header: form.post_header.trim() || null,
          roomtypes_url: form.roomtypes_url.trim() || null,
          rateplans_url: form.rateplans_url.trim() || null,
          booking_confirm_url: form.booking_confirm_url.trim() || null,
          inventory_update_url: form.inventory_update_url.trim() || null,
          rate_update_url: form.rate_update_url.trim() || null,
          inventory_rates_url: form.inventory_rates_url.trim() || null,
          booking_push_url: form.booking_push_url.trim() || null,
          no_show_update_url: form.no_show_update_url.trim() || null,
          allowed_ips: ipList.length > 0 ? ipList.join(',') : null
        })
      });
      const data = await res.json();
      if (!res.ok) { setSecError(data?.error || 'Failed to save'); return; }
      setSecMessage('IP whitelist saved.');
    } catch {
      setSecError('Network error');
    } finally {
      setSecSaving(false);
    }
  };

  const handleAddIp = () => {
    const ip = ipInput.trim();
    if (!ip) return;
    // Basic IP validation (v4 or v6 segment)
    if (!/^[\d.:\w]+$/.test(ip)) { setSecError('Invalid IP address format'); return; }
    if (ipList.includes(ip)) { setSecError('IP already in whitelist'); return; }
    setSecError('');
    setIpList([...ipList, ip]);
    setIpInput('');
  };

  const handleRemoveIp = (ip) => setIpList(ipList.filter((x) => x !== ip));

  // SSO helpers
  const handleAddSsoIp = () => {
    const ip = ssoIpInput.trim();
    if (!ip) return;
    if (!/^[\d.:\w]+$/.test(ip)) { setSsoError('Invalid IP address format'); return; }
    if (ssoIpList.includes(ip)) { setSsoError('IP already in whitelist'); return; }
    setSsoError('');
    setSsoIpList([...ssoIpList, ip]);
    setSsoIpInput('');
  };
  const handleRemoveSsoIp = (ip) => setSsoIpList(ssoIpList.filter((x) => x !== ip));

  const handleGenerateSsoKey = () => {
    const key = Array.from(crypto.getRandomValues(new Uint8Array(24))).map(b => b.toString(16).padStart(2, '0')).join('');
    setSsoServiceKey(key);
    setSsoKeyVisible(true);
  };

  const [ssoKeyEditing, setSsoKeyEditing] = useState(false);

  const handleSaveSso = async () => {
    setSsoSaving(true); setSsoError(''); setSsoMessage('');
    try {
      const res = await fetch('/api/admin/channel-manager', {
        method: 'POST', headers: headers(),
        body: JSON.stringify({
          channel_manager_name: form.channel_manager_name.trim() || 'EASEROOM',
          api_url: form.api_url.trim() || 'http://placeholder',
          sso_service_key: ssoServiceKey || null,
          sso_allowed_ips: ssoIpList.length > 0 ? ssoIpList.join(',') : null,
          cm_base_url: cmBaseUrl.trim() || null,
          cm_sso_login_url: cmSsoLoginUrl.trim() || null,
        })
      });
      const data = await res.json();
      if (!res.ok) { setSsoError(data?.error || 'Failed to save'); return; }
      setSsoMessage('SSO settings saved successfully.');
    } catch { setSsoError('Network error'); }
    finally { setSsoSaving(false); }
  };

  const handleCopyUrl = async () => {
    const ok = await copyText(callbackUrl);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const urlFieldStyle = { padding: '0.7rem', borderRadius: '6px', border: '1px solid #dcdde1', fontSize: '0.9rem' };
  const sectionStyle = {
    background: 'var(--theme-bg-light)', border: '1px solid var(--theme-border)', borderRadius: '8px',
    padding: '1.2rem 1.4rem', marginTop: '1.5rem'
  };

  if (loading) return <div className="page-shell" style={{ color: '#6b7280' }}>Loading...</div>;

  return (
    <div className="page-shell">
      <div style={{ fontSize: '0.78rem', color: '#6b7280', marginBottom: '0.6rem' }}>
        <span>Settings</span> <span style={{ color: '#cbd5e0' }}>›</span> <span style={{ color: 'var(--theme-primary-dark)', fontWeight: 600 }}>Channel Manager</span>
      </div>
      <h2 style={{ color: 'var(--theme-primary-dark)', marginBottom: '0.25rem' }}>Channel Manager Settings</h2>
      <p style={{ color: '#6b7280', fontSize: '0.9rem', marginBottom: '1.5rem' }}>Configure the external Channel Manager API integration (EaseRoom).</p>

      {error && <div className="error-banner" style={{ marginBottom: '1rem' }}>{error}</div>}
      {message && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.7rem 1rem', borderRadius: '6px', marginBottom: '1rem', fontSize: '0.9rem' }}>{message}</div>}

      <div className="filter-bar" style={{ maxWidth: '640px' }}>
        <div style={{ display: 'grid', gap: '1.2rem' }}>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Channel Manager Name</label>
            <input value={form.channel_manager_name} onChange={(e) => setForm({ ...form, channel_manager_name: e.target.value })} placeholder="EASEROOM" style={urlFieldStyle} />
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>API Base URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(max 500 chars)</span></label>
            <input value={form.api_url} onChange={(e) => setForm({ ...form, api_url: e.target.value.slice(0, 500) })} placeholder="http://220.225.104.135/PMS/PMSService.svc" maxLength={500} style={urlFieldStyle} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.api_url.length}/500</span>
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Get RoomTypes URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(max 500 chars)</span></label>
            <input value={form.roomtypes_url} onChange={(e) => setForm({ ...form, roomtypes_url: e.target.value.slice(0, 500) })} placeholder="http://220.225.104.135/PMS/PMSService.svc/BookingV1/RoomTypes" maxLength={500} style={urlFieldStyle} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.roomtypes_url.length}/500</span>
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Get RatePlans URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(max 500 chars)</span></label>
            <input value={form.rateplans_url} onChange={(e) => setForm({ ...form, rateplans_url: e.target.value.slice(0, 500) })} placeholder="http://220.225.104.135/PMS/PMSService.svc/BookingV1/RatePlans" maxLength={500} style={urlFieldStyle} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.rateplans_url.length}/500</span>
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Booking Status Confirm URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(max 500 chars)</span></label>
            <input value={form.booking_confirm_url} onChange={(e) => setForm({ ...form, booking_confirm_url: e.target.value.slice(0, 500) })} placeholder="http://220.225.104.135/PMS/PMSService.svc/BookingV1/BookingStatusConfirm" maxLength={500} style={urlFieldStyle} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.booking_confirm_url.length}/500</span>
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Inventory Update URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(max 500 chars)</span></label>
            <input value={form.inventory_update_url} onChange={(e) => setForm({ ...form, inventory_update_url: e.target.value.slice(0, 500) })} placeholder="http://220.225.104.135/PMS/PMSService.svc/BookingV1/InventoryUpdate" maxLength={500} style={urlFieldStyle} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.inventory_update_url.length}/500</span>
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Rate Update URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(max 500 chars)</span></label>
            <input value={form.rate_update_url} onChange={(e) => setForm({ ...form, rate_update_url: e.target.value.slice(0, 500) })} placeholder="http://220.225.104.135/PMS/PMSService.svc/BookingV1/RateUpdate" maxLength={500} style={urlFieldStyle} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.rate_update_url.length}/500</span>
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Get Inventory &amp; Rates URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(max 500 chars — used when a property's Rate Shopper own-rate source is set to EASEROOM Channel Manager)</span></label>
            <input value={form.inventory_rates_url} onChange={(e) => setForm({ ...form, inventory_rates_url: e.target.value.slice(0, 500) })} placeholder="http://220.225.104.135/PMS/PMSService.svc/BookingV1/GetInventoryAndRates" maxLength={500} style={urlFieldStyle} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.inventory_rates_url.length}/500</span>
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Booking Push URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(max 500 chars)</span></label>
            <input value={form.booking_push_url} onChange={(e) => setForm({ ...form, booking_push_url: e.target.value.slice(0, 500) })} placeholder="http://220.225.104.135/PMS/PMSService.svc/BookingV1/BookingPush" maxLength={500} style={urlFieldStyle} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.booking_push_url.length}/500</span>
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>No Show Update URL <span style={{ color: '#9ca3af', fontWeight: 400 }}>(max 500 chars — Booking.com only; use the Live host/path exactly as given, it differs from other endpoints' casing)</span></label>
            <input value={form.no_show_update_url} onChange={(e) => setForm({ ...form, no_show_update_url: e.target.value.slice(0, 500) })} placeholder="http://220.225.104.135/PMS/PMSService.svc/BookingV1/NoShowUpdate" maxLength={500} style={urlFieldStyle} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.no_show_update_url.length}/500</span>
          </div>

          <div style={{ display: 'grid', gap: '0.3rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)' }}>Post Header <span style={{ color: '#9ca3af', fontWeight: 400 }}>(JSON, max 500 chars)</span></label>
            <textarea value={form.post_header} onChange={(e) => setForm({ ...form, post_header: e.target.value.slice(0, 500) })} placeholder='{"Username":"test","Password":"test"}' maxLength={500} rows={3} style={{ padding: '0.7rem', borderRadius: '6px', border: '1px solid #dcdde1', fontSize: '0.875rem', fontFamily: 'monospace', resize: 'vertical' }} />
            <span style={{ fontSize: '0.75rem', color: '#9ca3af' }}>{form.post_header.length}/500</span>
          </div>

          <div>
            <button onClick={handleSubmit} disabled={saving} className="btn-primary" style={{ padding: '0.65rem 1.6rem' }}>
              {saving ? 'Saving...' : 'Submit'}
            </button>
          </div>

        </div>
      </div>

      {/* ---- Callback Security Section ---- */}
      <div style={{ maxWidth: '640px' }}>
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 0.1rem', color: '#1e3a5f', fontSize: '1rem' }}>Callback Security</h3>
          <p style={{ margin: '0 0 1.2rem', fontSize: '0.82rem', color: '#6b7280' }}>
            Configure the booking callback URL to share with EaseRoom. Optionally protect it with a secret token and/or IP whitelist.
          </p>

          {secError && <div className="error-banner" style={{ marginBottom: '0.8rem' }}>{secError}</div>}
          {secMessage && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.6rem 0.9rem', borderRadius: '6px', marginBottom: '0.8rem', fontSize: '0.85rem' }}>{secMessage}</div>}

          {/* Callback URL */}
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)', display: 'block', marginBottom: '0.3rem' }}>
              Booking Callback URL <span style={{ fontWeight: 400, color: '#6b7280' }}>(share this with EaseRoom)</span>
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                readOnly
                value={callbackUrl}
                style={{ ...urlFieldStyle, flex: 1, background: 'var(--theme-border-light)', color: '#1e40af', fontFamily: 'monospace', fontSize: '0.82rem' }}
              />
              <button
                onClick={handleCopyUrl}
                style={{ padding: '0.5rem 1rem', background: copied ? 'var(--theme-success)' : '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
              >
                {copied ? 'Copied!' : 'Copy URL'}
              </button>
            </div>
          </div>

          {/* IP Whitelist */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)', display: 'block', marginBottom: '0.3rem' }}>
              Source IP Whitelist
              <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '0.5rem' }}>
                (leave empty to allow any IP)
              </span>
            </label>

            {ipList.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
                {ipList.map((ip) => (
                  <span key={ip} style={{ background: '#eff6ff', border: '1px solid #bfdbfe', color: '#1e40af', borderRadius: '20px', padding: '0.2rem 0.75rem', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    {ip}
                    <button
                      onClick={() => handleRemoveIp(ip)}
                      style={{ background: 'none', border: 'none', color: 'var(--theme-danger)', cursor: 'pointer', fontWeight: 700, lineHeight: 1, padding: '0 2px', fontSize: '0.9rem' }}
                      title="Remove"
                    >×</button>
                  </span>
                ))}
              </div>
            )}

            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                value={ipInput}
                onChange={(e) => setIpInput(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleAddIp()}
                placeholder="e.g. 203.x.x.1"
                style={{ ...urlFieldStyle, flex: 1, fontSize: '0.85rem' }}
              />
              <button onClick={handleAddIp} style={{ padding: '0.5rem 1rem', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>
                + Add IP
              </button>
            </div>
            {ipList.length === 0 && (
              <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '0.4rem 0 0' }}>No IPs in whitelist — all source IPs allowed.</p>
            )}
          </div>

          <button onClick={handleSaveSecurity} disabled={secSaving} className="btn-primary" style={{ padding: '0.55rem 1.4rem', fontSize: '0.875rem' }}>
            {secSaving ? 'Saving...' : 'Save IP Whitelist'}
          </button>
        </div>

        {/* ---- SSO / Cross-Auth Configuration ---- */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 0.1rem', color: '#1e3a5f', fontSize: '1rem' }}>SSO / Cross-Auth Configuration</h3>
          <p style={{ margin: '0 0 1.2rem', fontSize: '0.82rem', color: '#6b7280' }}>
            Configure the service key and IP whitelist for SSO &amp; User Provisioning APIs used by the CM app.
          </p>

          {ssoError && <div className="error-banner" style={{ marginBottom: '0.8rem' }}>{ssoError}</div>}
          {ssoMessage && <div style={{ background: '#f0fdf4', border: '1px solid #86efac', color: '#166534', padding: '0.6rem 0.9rem', borderRadius: '6px', marginBottom: '0.8rem', fontSize: '0.85rem' }}>{ssoMessage}</div>}

          {/* CM Base URL */}
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)', display: 'block', marginBottom: '0.3rem' }}>
              Channel Manager Base URL <span style={{ fontWeight: 400, color: '#6b7280' }}>(CM portal URL)</span>
            </label>
            <input
              value={cmBaseUrl}
              onChange={(e) => setCmBaseUrl(e.target.value)}
              placeholder="https://portal.easeroom.co"
              style={{ ...urlFieldStyle, width: '100%', boxSizing: 'border-box' }}
            />
          </div>

          {/* CM SSO Login URL — EaseRoom SSO endpoint for PMS→CM redirect */}
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)', display: 'block', marginBottom: '0.3rem' }}>
              CM SSO Login URL <span style={{ fontWeight: 400, color: '#6b7280' }}>(EaseRoom SSO endpoint — "Switch to CM" calls this)</span>
            </label>
            <input
              value={cmSsoLoginUrl}
              onChange={(e) => setCmSsoLoginUrl(e.target.value)}
              placeholder="https://portal.easeroom.co/EaseroomCM/SSO/LoginFromPMS"
              style={{ ...urlFieldStyle, width: '100%', boxSizing: 'border-box' }}
            />
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              PMS will POST to this URL with <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>?Username=&lt;user&gt;&amp;Hotelid=&lt;easeroom_hotel_id&gt;</code> and <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>X-Service-Key</code> header.
            </p>
          </div>

          {/* PMS SSO Login URL — read-only, for CM team to configure on their side */}
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)', display: 'block', marginBottom: '0.3rem' }}>
              PMS SSO Login URL <span style={{ fontWeight: 400, color: '#059669' }}>(share with CM team — for "Switch to PMS" from CM)</span>
            </label>
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input
                readOnly
                value={`${window.location.origin}/api/v1/cross-auth/login-from-cm`}
                style={{ ...urlFieldStyle, flex: 1, background: 'var(--theme-border-light)', color: '#1e40af', fontFamily: 'monospace', fontSize: '0.82rem' }}
              />
              <button
                onClick={() => copyText(`${window.location.origin}/api/v1/cross-auth/login-from-cm`)}
                style={{ padding: '0.5rem 1rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem', whiteSpace: 'nowrap' }}
              >Copy</button>
            </div>
            <p style={{ fontSize: '0.75rem', color: '#6b7280', margin: '0.3rem 0 0' }}>
              CM should POST to this URL with <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>?Username=&lt;user&gt;&amp;Hotelid=&lt;easeroom_hotel_id&gt;</code> and <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>X-Service-Key</code> header. Returns a <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>redirect_url</code> for auto-login into PMS.
            </p>
          </div>

          {/* SSO Service Key */}
          <div style={{ marginBottom: '1.2rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)', display: 'block', marginBottom: '0.3rem' }}>
              SSO Service Key
              <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '0.5rem' }}>
                (CM sends as <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>X-Service-Key</code> header)
              </span>
            </label>
            {ssoServiceKey || ssoKeyEditing ? (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <input
                  readOnly={!ssoKeyEditing}
                  type={ssoKeyVisible || ssoKeyEditing ? 'text' : 'password'}
                  value={ssoServiceKey}
                  onChange={(e) => ssoKeyEditing && setSsoServiceKey(e.target.value.trim())}
                  placeholder={ssoKeyEditing ? 'Paste the CM-provided service key here' : ''}
                  style={{ ...urlFieldStyle, flex: 1, background: ssoKeyEditing ? '#fff' : 'var(--theme-border-light)', fontFamily: 'monospace', fontSize: '0.82rem', minWidth: '200px' }} />
                {!ssoKeyEditing && (
                  <button onClick={() => setSsoKeyVisible(!ssoKeyVisible)} style={{ padding: '0.5rem 0.8rem', border: '1px solid #d1d5db', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '0.82rem' }}>
                    {ssoKeyVisible ? 'Hide' : 'Show'}
                  </button>
                )}
                {!ssoKeyEditing && (
                  <button onClick={() => copyText(ssoServiceKey)} style={{ padding: '0.5rem 0.8rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>Copy</button>
                )}
                {ssoKeyEditing ? (
                  <button onClick={() => setSsoKeyEditing(false)} style={{ padding: '0.5rem 0.8rem', background: '#16a34a', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>Done</button>
                ) : (
                  <button onClick={() => { setSsoKeyEditing(true); setSsoKeyVisible(true); }} style={{ padding: '0.5rem 0.8rem', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>Edit / Paste</button>
                )}
                <button onClick={handleGenerateSsoKey} style={{ padding: '0.5rem 0.8rem', background: 'var(--theme-warning)', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.82rem' }}>Regenerate</button>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <span style={{ fontSize: '0.85rem', color: '#6b7280', fontStyle: 'italic' }}>No service key configured</span>
                <button onClick={handleGenerateSsoKey} style={{ padding: '0.5rem 1rem', background: '#2563eb', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>Generate Key</button>
                <button onClick={() => { setSsoKeyEditing(true); setSsoKeyVisible(true); }} style={{ padding: '0.5rem 1rem', background: '#fff', color: '#374151', border: '1px solid #d1d5db', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>Paste Existing Key</button>
              </div>
            )}
          </div>

          {/* SSO IP Whitelist */}
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ fontWeight: 600, fontSize: '0.875rem', color: 'var(--theme-text)', display: 'block', marginBottom: '0.3rem' }}>
              SSO API IP Whitelist
              <span style={{ fontWeight: 400, color: '#6b7280', marginLeft: '0.5rem' }}>(CM server IPs allowed to call provisioning/token APIs)</span>
            </label>
            {ssoIpList.length > 0 && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.4rem', marginBottom: '0.6rem' }}>
                {ssoIpList.map((ip) => (
                  <span key={ip} style={{ background: '#fef3c7', border: '1px solid #fbbf24', color: '#92400e', borderRadius: '20px', padding: '0.2rem 0.75rem', fontSize: '0.82rem', display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
                    {ip}
                    <button onClick={() => handleRemoveSsoIp(ip)} style={{ background: 'none', border: 'none', color: '#b91c1c', cursor: 'pointer', fontWeight: 700, lineHeight: 1, padding: '0 2px', fontSize: '0.9rem' }} title="Remove">x</button>
                  </span>
                ))}
              </div>
            )}
            <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
              <input value={ssoIpInput} onChange={(e) => setSsoIpInput(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && handleAddSsoIp()}
                placeholder="e.g. 103.21.58.100" style={{ ...urlFieldStyle, flex: 1, fontSize: '0.85rem' }} />
              <button onClick={handleAddSsoIp} style={{ padding: '0.5rem 1rem', background: '#059669', color: '#fff', border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.85rem' }}>+ Add IP</button>
            </div>
            {ssoIpList.length === 0 && <p style={{ fontSize: '0.78rem', color: '#9ca3af', margin: '0.4rem 0 0' }}>No IPs — SSO APIs accept all sources (not recommended for production).</p>}
          </div>

          <button onClick={handleSaveSso} disabled={ssoSaving} className="btn-primary" style={{ padding: '0.55rem 1.4rem', fontSize: '0.875rem' }}>
            {ssoSaving ? 'Saving...' : 'Save SSO Settings'}
          </button>
        </div>

        {/* ---- PMS Exposed APIs for EaseRoom ---- */}
        <div style={sectionStyle}>
          <h3 style={{ margin: '0 0 0.1rem', color: '#1e3a5f', fontSize: '1rem' }}>PMS Exposed APIs</h3>
          <p style={{ margin: '0 0 1rem', fontSize: '0.82rem', color: '#6b7280' }}>
            These are the PMS API endpoints available for the EaseRoom Channel Manager to call. Share these with the CM team.
          </p>

          <div style={{ fontSize: '0.82rem', display: 'grid', gap: '0.6rem' }}>
            <div style={{ fontWeight: 700, color: '#059669', marginBottom: '0.2rem' }}>Booking & Inventory APIs</div>
            {[
              { label: 'Booking Callback', url: `${window.location.origin}/api/v1/channel-manager/booking-callback`, method: 'POST' },
              { label: 'Inventory Push (PMS → CM)', note: 'Outbound — PMS pushes to CM URL configured above' },
            ].map((api, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.4rem 0.6rem', background: '#f0fdf4', borderRadius: '6px', border: '1px solid #bbf7d0' }}>
                {api.method && <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', background: '#059669', borderRadius: '3px', padding: '1px 6px' }}>{api.method}</span>}
                <span style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.78rem', color: '#1e3a5f', wordBreak: 'break-all' }}>{api.url || api.note}</span>
                {api.url && (
                  <button onClick={() => copyText(api.url)} style={{ padding: '0.2rem 0.5rem', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', cursor: 'pointer', fontSize: '0.72rem' }}>Copy</button>
                )}
              </div>
            ))}

            <div style={{ fontWeight: 700, color: '#1e40af', marginTop: '0.5rem', marginBottom: '0.2rem' }}>SSO & User Provisioning APIs</div>
            {[
              { label: 'Create User', url: `${window.location.origin}/api/v1/users/provision`, method: 'POST' },
              { label: 'Update User', url: `${window.location.origin}/api/v1/users/provision/:id`, method: 'PUT' },
              { label: 'List CM Users', url: `${window.location.origin}/api/v1/users/provision?easeroom_hotel_id=<id>`, method: 'GET' },
              { label: 'Get Token', url: `${window.location.origin}/api/v1/cross-auth/token`, method: 'POST' },
              { label: 'Refresh Token', url: `${window.location.origin}/api/v1/cross-auth/refresh`, method: 'POST' },
              { label: 'Cross-App Launch', url: `${window.location.origin}/api/v1/cross-auth/launch`, method: 'POST' },
              { label: 'Login from CM (SSO)', url: `${window.location.origin}/api/v1/cross-auth/login-from-cm`, method: 'POST' },
              { label: 'Validate SSO Token', url: `${window.location.origin}/api/v1/cross-auth/validate-sso`, method: 'POST' },
            ].map((api, i) => (
              <div key={i} style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', padding: '0.4rem 0.6rem', background: '#eff6ff', borderRadius: '6px', border: '1px solid #bfdbfe' }}>
                <span style={{ fontSize: '0.7rem', fontWeight: 700, color: '#fff', background: api.method === 'GET' ? '#2563eb' : api.method === 'PUT' ? '#d97706' : '#7c3aed', borderRadius: '3px', padding: '1px 6px' }}>{api.method}</span>
                <span style={{ fontSize: '0.72rem', fontWeight: 600, color: '#475569', minWidth: '100px' }}>{api.label}</span>
                <span style={{ flex: 1, fontFamily: 'monospace', fontSize: '0.75rem', color: '#1e40af', wordBreak: 'break-all' }}>{api.url}</span>
              </div>
            ))}

            <div style={{ fontSize: '0.75rem', color: '#6b7280', marginTop: '0.3rem', padding: '0.4rem 0.6rem', background: '#fef3c7', borderRadius: '6px', border: '1px solid #fbbf24' }}>
              <b>Auth:</b> SSO/Provisioning APIs require <code style={{ background: '#e5e7eb', padding: '1px 4px', borderRadius: '3px' }}>X-Service-Key</code> header + IP whitelisting (configured above).
              These APIs only accept requests from whitelisted CM server IPs.
            </div>
          </div>
        </div>
      </div>

    </div>
  );
}

export default ChannelManagerSettings;
